import { ChangeEvent, useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Link, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import EntitySelectBox from '@components/EntitySelectBox'
import EntitySelector from '@components/EntitySelector'

import useProcessingFee from '@hooks/useProcessingFee'

import useCreateOrEditProcessingFeeModal from './hooks/useCreateOrEditProcessingFeeModal'

const MODAL_KEY = 'CreateOrEditProcessingFeeModal'

type ModalCallbacks = {
  closeModal: () => void,
  createOrUpdateProcessingFee: (payload: HandleCallbackActionParams) => Promise<object>,
  deleteProcessingFee: (payload: HandleCallbackActionParams) => Promise<object>,
  showModal: boolean,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

type UserProcessingFeePayload = ReturnType<typeof useProcessingFee>

type ConfirmDeleteParams = {
  callbacks: ModalCallbacks,
  deleteFn: UserProcessingFeePayload['callbacks']['deleteProcessingFee'],
}

const confirmDelete = (params: ConfirmDeleteParams) => {
  const { callbacks, deleteFn } = params
  const { closeModal, deleteProcessingFee } = callbacks

  const deleteProcessingFeePayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Processing Fee Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, delete it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Delete Processing Fee?',
    })
    .then(({ value }) => {
      if (value){
        deleteProcessingFee(deleteProcessingFeePayload)
      }
    })
}

const CreateOrEditProcessingFeeModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createOrUpdateProcessingFee } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { processingFee } = modalPayload

  const {
    callbacks: {
      createOrUpdateProcessingFee: createOrUpdateFn,
      deleteProcessingFee: deleteFn,
      hideProductSelector,
      setServiceId,
      showProductSelector,
    },
    creating,
    deleting,
    entityNameStrings,
    formPayload,
    serviceId,
    showProductSelect,
  } = useCreateOrEditProcessingFeeModal(processingFee)

  const {
    entityState,
    entityState: {
      billable_id,
      billable_type,
      debtor_id,
      debtor_type,
      fee_type,
      global,
      id,
      value,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const processingFeePayload = {
    callbacks: {
      action: createOrUpdateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Processing Fee ${processingFee.id ? 'Updated' : 'Created'}`,
  }

  const isEditing = !!id
  const updateOrCreateButtonText = id ? 'Save Processing Fee' : 'Create Processing Fee'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Processing Fee" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Service" errorMessage={errors.billable_id}>
            <EntitySelectBox
              // @ts-expect-error TS2322
              disabled={isEditing}
              entityKey="services"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setServiceId(e.target.value)}
              value={serviceId}
            />
          </Form.Field>

          {!showProductSelect && !isEditing && (
            <Button
              buttonStyle="secondaryUtility"
              marginTop="large"
              onClick={() => showProductSelector()}
              size="medium"
            >
              Select a Product
            </Button>
          )}

          {showProductSelect && (
            <EntitySelector
              callbacks={{
                selectItem: product => (
                  setEntityState({ billable_id: product ? product.id : null, billable_type: 'ServiceProduct' })
                ),
              }}
              disabled={isEditing}
              endpoint="service_products"
              entityKey="serviceProducts"
              label="Product"
              labelRight={!isEditing ? (
                <Link onClick={() => hideProductSelector()} textProps={{ fontSize: 'xsmall' }}>
                  Hide Product
                </Link>
              ) : null}
              marginTop="large"
              searchRequestOptions={{ service_id: serviceId }}
              selectedItemId={billable_type === 'ServiceProduct' ? billable_id : undefined}
            />
          )}

          <Form.Field label="* Debtor Type" marginTop="large" errorMessage={errors.debtor_type}>
            {/*
             // @ts-expect-error TS2322 */}
            <select disabled={isEditing} name="debtor_type" value={debtor_type} {...handlers}>
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              <option value="Brand">Brand</option>
              <option value="Provider">Provider</option>
            </select>
          </Form.Field>

          <Form.Field label="Global" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              disabled={isEditing}
              name="global"
              value={global}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          {debtor_type && !global && (
            <EntitySelector
              callbacks={{ selectItem: entity => setEntityState({ debtor_id: entity?.id }) }}
              disabled={isEditing}
              // @ts-expect-error TS7053
              entityKey={entityNameStrings[debtor_type]}
              label={debtor_type}
              marginTop="large"
              selectedItemId={debtor_id}
            />
          )}

          <SectionDivider boxProps={{ marginY: 'large' }}>Fee</SectionDivider>

          <Form.Field label="* Fee Type">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="fee_type" value={fee_type} {...handlers}>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </select>
          </Form.Field>

          <Form.Field label="* Fee Amount" marginTop="large">
            <input name="value" type="number" value={value} {...handlers} />
          </Form.Field>

          {isEditing && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete({ callbacks, deleteFn })}
                size="medium"
              >
                Delete Processing Fee
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createOrUpdateProcessingFee(processingFeePayload)}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProcessingFeeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
